import { useMemo } from 'react';
import { connect } from 'react-redux';

import { IKnmAttachment, IKnmAttachmentDownload } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { mapErknmPlanStatusByStatusTitle } from 'APIPackage/PlanListModule/constants/mapErknmPlanStatusByStatusTitle';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { ErknmPlanStatusTitlesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusTitlesEnum';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { IKnmWebNotificationInfo } from 'APIPackage/KnmModule/interfaces/IKnmWebNotificationInfo';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';

import { IPlanHistoryItem } from '../interfaces/IPlanHistoryItem';
import {
  getPlanDataFullNameWhoConsideredPlan,
  getPlanDataPositionWhoConsideredPlan,
  getPlanDocuments,
  getPlanApprovedHistoryChanges,
  getPlanSignedHistoryChanges,
  getPlanIsLoadingHistory,
  getPlanIsLoadingDocuments,
  getPlanVersions,
  getPlanResponsesFromWeb,
  getPlanHistoryChanges,
} from '../selectors/planSelectors';
import { PlanSideInfoItem } from './PlanSideInfoItem';
import styles from './PlanSideInfo.module.scss';

interface IPlanSideInfoStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  fullNameWhoConsideredPlan: string | null | undefined;
  positionWhoConsideredPlan: string | null | undefined;
  documents: IKnmAttachmentDownload[] | undefined;
  isLoading: boolean;
  signedHistoryChanges: IPlanHistoryItem[];
  planVersions: IKnmAttachment[] | null | undefined;
  responsesFromWeb: IKnmWebNotificationInfo[] | null | undefined;
  isLoadingHistory: boolean;
  isLoadingDocuments: boolean;
  approvedHistoryChanges: IPlanHistoryItem[];
  historyChanges: IKnmHistoryItemChanges[] | undefined;
}

const PlanSideInfoWithoutStore = ({
  documents,
  fullNameWhoConsideredPlan,
  isLoading,
  isLoadingDocuments,
  isLoadingHistory,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  planVersions,
  positionWhoConsideredPlan,
  responsesFromWeb,
  signedHistoryChanges,
  approvedHistoryChanges,
  historyChanges,
}: IPlanSideInfoStoreProps) => {
  const editedApprovedHistoryChanges: IPlanHistoryItem[] = useMemo(() => {
    return approvedHistoryChanges.reduce((editedItems: IPlanHistoryItem[], item) => {
      const itemStatus: ErknmPlanStatusesEnum =
        mapErknmPlanStatusByStatusTitle[item.title as ErknmPlanStatusTitlesEnum];

      if (itemStatus === ErknmPlanStatusesEnum.CONSIDERED) {
        const prosecutorItem = {
          ...item,
          name: `${fullNameWhoConsideredPlan} - ${positionWhoConsideredPlan}`,
        };

        if (isSupervisor) {
          editedItems.push(prosecutorItem);

          return editedItems;
        }
        if (isProsecutorOrSuperProsecutor) {
          editedItems.push(prosecutorItem);
          editedItems.push(item);

          return editedItems;
        }
      }
      editedItems.push(item);

      return editedItems;
    }, []);
  }, [
    approvedHistoryChanges,
    isSupervisor,
    isProsecutorOrSuperProsecutor,
    fullNameWhoConsideredPlan,
    positionWhoConsideredPlan,
  ]);

  return (
    <div className={styles.PlanSideInfo}>
      <PlanSideInfoItem
        name="История согласования"
        data={{
          type: 'history',
          history: editedApprovedHistoryChanges,
        }}
        isLoading={isLoadingHistory}
        id="approvedHistoryBlock"
      />
      <PlanSideInfoItem
        name="История подписания"
        data={{
          type: 'history',
          history: signedHistoryChanges,
        }}
        isLoading={isLoadingHistory}
        id="signedHistoryBlock"
      />
      <PlanSideInfoItem
        name={'Статус обмена с АИК "Надзор-Web"'}
        data={{
          type: 'web',
          responses: responsesFromWeb,
        }}
        isLoading={isLoading}
        id="responsesFromWebBlock"
      />
      <PlanSideInfoItem
        name="Список документов"
        data={{
          type: 'documents',
          docs: documents,
        }}
        isLoading={isLoadingDocuments}
        id="documentsBlock"
      />
      <PlanSideInfoItem
        name="Версии плана"
        data={{
          type: 'versions',
          docs: planVersions,
        }}
        isLoading={isLoading}
        id="versionsBlock"
      />
      <PlanSideInfoItem
        name="История изменений"
        data={{
          type: 'historyChanges',
          docs: historyChanges,
        }}
        isLoading={isLoading}
        id="versionsBlock"
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPlanSideInfoStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  fullNameWhoConsideredPlan: getPlanDataFullNameWhoConsideredPlan(state),
  positionWhoConsideredPlan: getPlanDataPositionWhoConsideredPlan(state),
  documents: getPlanDocuments(state),
  signedHistoryChanges: getPlanSignedHistoryChanges(state),
  planVersions: getPlanVersions(state),
  responsesFromWeb: getPlanResponsesFromWeb(state),
  isLoadingHistory: getPlanIsLoadingHistory(state),
  isLoadingDocuments: getPlanIsLoadingDocuments(state),
  isLoading: getCoreIsLoading(state),
  approvedHistoryChanges: getPlanApprovedHistoryChanges(state),
  historyChanges: getPlanHistoryChanges(state),
});

export const PlanSideInfo = connect(mapStateToProps)(PlanSideInfoWithoutStore);
